import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #f8f8f8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1.5em;
  min-height: 400px;
  gap: 32px;

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 24px;
    padding: 1.25em;
  }

  @media (max-width: ${breakpoints.phone}px) {
    overflow: auto;
    gap: 16px;
    padding: 1em 0.75em;
  }
`;

const Actions = styled.div`
  ${actionContainerStyles};
  margin-right: 0;
  // border: 1px solid ${({ colors }) => colors.border};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    margin: 0;
    padding: 12px 16px;
    list-style: none;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: ${breakpoints.tablet}px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const SummaryTable = styled.table`
  ${adminTable};
  width: min(750px, 100%);
  margin-bottom: 10px;

  thead,
  tfoot {
    display: none;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.5em 10px;
  }

  strong {
    font-weight: 600;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  ${adminTable};

  thead th {
    ${adminTableHeaderCell};
    background: #fff;
    border-bottom: 1px solid #ccc;
    text-align: left;
    font-weight: 600;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.5em 10px;
    vertical-align: top;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    tbody td.actionCell {
      width: 1%;
      white-space: nowrap;
      padding: 0.5em 8px;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
    }
  }
`;

const LinkButton = styled.a`
  background-color: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 3px;
  margin-left: 0.5em;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${LINK_HOVER_COLOR};
    text-decoration: none;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    margin-left: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    font-size: 11px;
    white-space: nowrap;
    min-width: 72px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 4px 8px;
  }
`;

const FormField = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    color: ${({ colors }) => colors.labelText};
    font-weight: 600;
  }
  input[type="text"], input[type="email"], select {
    width: 100%;
    padding: 0.5em;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
  }
`;

const RadioGroup = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.5em;
    color: ${({ colors }) => colors.labelText};
    font-size: 0.95em;
    strong { font-weight: 600; }
  }
  input { margin-right: 0.5em; }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1em;
`;

const Button = styled.button`
  padding: 0.6em 1.2em;
  font-size: 1em;
  border-radius: 4px;
  border: ${({ primary, colors }) =>
    primary ? "none" : `1px solid ${colors.border}`};
  background: ${({ primary, colors }) =>
    primary ? colors.buttonBackground : colors.cardBackground};
  color: ${({ primary }) => (primary ? "white" : "inherit")};
  cursor: pointer;
  &:hover {
    background: ${({ primary, colors }) =>
      primary ? colors.buttonHoverBackground : colors.buttonBackground};
    color: white;
  }
`;

export default function SubscriptionAdminsContent({ colors }) {
  const customer = useSelector((s) => s.auth.selected_subscriptions) || {};
  const customerId = customer.customer_id;
  const location = useLocation();
  const locationMode = location.state?.mode;
  const renderUserName = (user) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name || <em>PENDING REGISTRATION</em>;
  };

  const [mode, setMode]      = useState("list");       // "list" | "add"
  const [admins, setAdmins]  = useState([]);
  const [adminsPend, setAdminsPend] = useState([]);
  const [accts, setAccts]    = useState([]);
  const [acctsPend, setAcctsPend]   = useState([]);
  const [maxAdmins, setMaxAdmins]   = useState(0);
  const [maxAccts, setMaxAccts]     = useState(0);

  // new Add‐form fields:
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [role,      setRole]      = useState("");

  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [inviteOpen,  setInviteOpen]    = useState(false);
  const [resultOpen,  setResultOpen]    = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [modalTitle,   setModalTitle]     = useState("");
  const [modalMessage, setModalMessage]   = useState("");
  const [modalStatus,  setModalStatus]    = useState("Success");

  useEffect(() => {
    const nextMode = locationMode || "list";
    setMode(nextMode);
    if (nextMode === "list") {
      setFirstName("");
      setLastName("");
      setEmail("");
      setRole("");
    }
  }, [location.key, locationMode]);

  const refreshAdminUsers = useCallback(() => {
    if (!customerId) return;
    API.getAdminUsers({ customer_id: customerId })
      .then((resp) => {
        setAdmins((resp.admins || []).map(u => ({ ...u, pending: 0 })));
        setAdminsPend((resp.admins_pending || []).map(u => ({ ...u, pending: 1 })));
        setAccts((resp.accountants || []).map(u => ({ ...u, pending: 0 })));
        setAcctsPend((resp.accountants_pending || []).map(u => ({ ...u, pending: 1 })));
        const ma = Number(resp.max_admins?.max_admins || 0);
        const mc = Number(resp.max_admins?.max_accountants || 0);
        setMaxAdmins(ma);
        setMaxAccts(mc);
      })
      .catch(console.error);
  }, [customerId]);

  useEffect(() => {
    refreshAdminUsers();
  }, [refreshAdminUsers]);

  const remainingAdmins = maxAdmins - admins.length;
  const remainingAccts  = maxAccts  - accts.length;

  // ---- revoke ----
  const onRevokeClick = (user, isAdmin) => {
    setPendingAction({ type: "revoke", user, isAdmin });
    setConfirmOpen(true);
  };
  const handleRevokeConfirm = () => {
    const { user, isAdmin } = pendingAction;
    setModalStatus("Success");
    const payload = {
      customer_id: customerId,
      user_id:     user.user_id,
      email:       user.email,
      role:        isAdmin ? "subscription_admin" : "subscription_accountant",
      ...(user.pending && { is_pending: 1 }),
    };
    API.revokeAdminUsers(payload)
      .then((resp) => {
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setModalTitle(status === "Failed" ? "Error" : "Revoked");
        setModalMessage(resp.message || (status === "Failed" ? "Failed to revoke" : "User revoked"));
        setModalStatus(status);
        if (isAdmin) {
          user.pending
            ? setAdminsPend(a => a.filter(u => u.user_id !== user.user_id))
            : setAdmins(a => a.filter(u => u.user_id !== user.user_id));
        } else {
          user.pending
            ? setAcctsPend(a => a.filter(u => u.user_id !== user.user_id))
            : setAccts(a => a.filter(u => u.user_id !== user.user_id));
        }
      })
      .catch((err) => {
        setModalTitle("Error");
        setModalMessage(err.message || "Failed to revoke");
        setModalStatus("Failed");
      })
      .finally(() => {
        setConfirmOpen(false);
        setResultOpen(true);
        setPendingAction(null);
      });
  };

  // ---- invite ----
  const onInviteClick = () => {
    setPendingAction({ type: "add", firstName, lastName, email, role });
    setInviteOpen(true);
  };
  const handleInviteConfirm = () => {
    const { firstName: f, lastName: l, email: e, role: r } = pendingAction;
    setModalStatus("Success");
      API.addAdminUsers({
        customer_id: customerId,
        first_name:  f,
        last_name:   l,
        email:       e,
        role:        r,
      })
        .then((resp) => {
          const status = resp?.status === "Failed" ? "Failed" : "Success";
          setModalTitle(status === "Failed" ? "Error" : "Invited");
          setModalMessage(resp.message || (status === "Failed" ? "Failed to send invitation" : "Invitation sent"));
          setModalStatus(status);
          const stub = {
            user_id:    Date.now().toString(),
            first_name: f,
            last_name:  l,
            email:      e,
            pending:    1,
          };
          if (r === "subscription_admin") setAdminsPend(a => [...a, stub]);
          else                             setAcctsPend(a => [...a, stub]);
          if (status !== "Failed") refreshAdminUsers();
        })
      .catch((err) => {
        setModalTitle("Error");
        setModalMessage(err.message || "Failed to invite");
      })
      .finally(() => {
        setInviteOpen(false);
        setResultOpen(true);
        setMode("list");
        setFirstName("");
        setLastName("");
        setEmail("");
        setRole("");
        setPendingAction(null);
      });
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  // ---- modals ----
  const revokeModal = (
    <ConfirmationModal
      show={confirmOpen}
      title="Confirm Revocation"
      message="Are you sure you want to revoke this user?"
      onCancel={() => setConfirmOpen(false)}
      onConfirm={handleRevokeConfirm}
    />
  );
  const inviteModal = (
    <ConfirmationModal
      show={inviteOpen}
      title="Confirm Invitation"
      message={`Invite ${pendingAction?.email} as ${pendingAction?.role === "subscription_admin" ? "Admin" : "Accountant"}?`}
      onCancel={() => setInviteOpen(false)}
      onConfirm={handleInviteConfirm}
    />
  );
  const resultModal = (
    <ConfirmationModal
      show={resultOpen}
      title={modalTitle}
      message={modalMessage}
      single
      onCancel={handleResultClose}
      onConfirm={handleResultClose}
      status={modalStatus}
    />
  );

  return (
    <>
      {revokeModal}
      {inviteModal}
      {resultModal}

      {mode === "list" ? (
        <Container>
          <ResponsiveActions
            colors={colors}
            mobileToggleTop={-10}
            mobileToggleLeft={-15}
            renderActions={() => (
              <Actions colors={colors}>
                <h3>Actions</h3>
                <ul>
                  <li>
                    <a onClick={() => setMode("add")}>Add</a>
                  </li>
                </ul>
              </Actions>
            )}
          />
          <View>
            <TableWrapper>
              <SummaryTable>
                <tbody>
                  <tr>
                    <td><strong>Admin Slots Remaining:</strong></td>
                    <td>{remainingAdmins} of {maxAdmins}</td>
                  </tr>
                  <tr>
                    <td><strong>Accountant Slots Remaining:</strong></td>
                    <td>{remainingAccts} of {maxAccts}</td>
                  </tr>
                </tbody>
              </SummaryTable>
            </TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-mail</th>
                  <th>Role</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {admins.map(u => (
                  <tr key={u.user_id}>
                    <td>{u.first_name} {u.last_name}</td>
                    <td>{u.email}</td>
                    <td>Access to all administrative features of the subscription.</td>
                    <td className="actionCell">
                      <LinkButton colors={colors} onClick={() => onRevokeClick(u, true)}>
                        Revoke
                      </LinkButton>
                    </td>
                  </tr>
                ))}
                {adminsPend.map((u,i) => (
                  <tr key={`pend-admin-${i}`}>
                    <td>{renderUserName(u)}</td>
                    <td>{u.email}</td>
                    <td>Access to all administrative features of the subscription.</td>
                    <td className="actionCell">
                      <LinkButton colors={colors} onClick={() => onRevokeClick(u, true)}>
                        Revoke
                      </LinkButton>
                    </td>
                  </tr>
                ))}
                {accts.map(u => (
                  <tr key={u.user_id}>
                    <td>{u.first_name} {u.last_name}</td>
                    <td>{u.email}</td>
                    <td>Can view subscription details and implementations.</td>
                    <td className="actionCell">
                      <LinkButton colors={colors} onClick={() => onRevokeClick(u, false)}>
                        Revoke
                      </LinkButton>
                    </td>
                  </tr>
                ))}
                {acctsPend.map((u,i) => (
                  <tr key={`pend-acct-${i}`}>
                    <td>{renderUserName(u)}</td>
                    <td>{u.email}</td>
                    <td>Can view subscription details and implementations.</td>
                    <td className="actionCell">
                      <LinkButton colors={colors} onClick={() => onRevokeClick(u, false)}>
                        Revoke
                      </LinkButton>
                    </td>
                  </tr>
                ))}
                {(admins.length + accts.length) === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: "1em" }}>
                      No users assigned.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </View>
        </Container>
      ) : (
        <Container>
          <ResponsiveActions
            colors={colors}
            mobileToggleTop={-10}
            mobileToggleLeft={-25}
            renderActions={() => (
              <Actions colors={colors}>
                <h3>Actions</h3>
                <ul>
                  <li><a onClick={() => setMode("list")}>Back to list</a></li>
                </ul>
              </Actions>
            )}
          />
          <View>
            <h1 style={{ color: colors.labelText }}>Add Administrator</h1>
            <form onSubmit={(e) => { e.preventDefault(); onInviteClick(); }}>
              <FormField colors={colors}>
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </FormField>
              <FormField colors={colors}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </FormField>
              <FormField colors={colors}>
                <label htmlFor="adm-email">Email</label>
                <input
                  id="adm-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormField>
              <RadioGroup colors={colors}>
                <label>
                  <input
                    type="radio"
                    name="role"
                    value="subscription_admin"
                    checked={role === "subscription_admin"}
                    onChange={() => setRole("subscription_admin")}
                    disabled={remainingAdmins <= 0}
                  />
                  <strong>
                    Subscription Admin – all administrative features
                    ({remainingAdmins} of {maxAdmins} slots left)
                  </strong>
                </label>
                <label>
                  <input
                    type="radio"
                    name="role"
                    value="subscription_accountant"
                    checked={role === "subscription_accountant"}
                    onChange={() => setRole("subscription_accountant")}
                    disabled={remainingAccts <= 0}
                  />
                  <strong>
                    Subscription Accountant – view details & implementations
                    ({remainingAccts} of {maxAccts} slots left)
                  </strong>
                </label>
              </RadioGroup>
              <ButtonRow>
                <Button
                  type="button"
                  colors={colors}
                  onClick={() => setMode("list")}
                >
                  Cancel
                </Button>
                <Button
                  primary
                  type="submit"
                  colors={colors}
                  disabled={
                    !firstName || !lastName || !email || !role
                  }
                >
                  Submit
                </Button>
              </ButtonRow>
            </form>
          </View>
        </Container>
      )}
    </>
  );
}
